<script setup lang="ts">
import { onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGoogleMaps } from '@/composables/googleMaps'
import Icon from '@/components/partials/Icon.vue'
import Button from '@/components/partials/Buttons/Button/Button.vue'
import ModalNuevo from '@/components/Modals/ModalNuevo/ModalNuevo.vue'
import AnimatedFoodMarker from './AnimatedFoodMarker/AnimatedFoodMarker.vue'
import type { Location } from '@/types/Location'
import { useSessionStore } from "@/stores/useSessionStore"
import { getLocale } from "@/router/utils/getLocale"
// import { useGa } from '@/composables/useGa'

const props = defineProps<{
  open: boolean
}>()

// const ga = useGa()

const { getCurrentLocation } = useSessionStore()

const currentLocation = getCurrentLocation as Location | null

const emit = defineEmits([ 'submit' ])

const state = reactive({
  lat: currentLocation?.coordinates?.lat,
  lng: currentLocation?.coordinates?.lng,
  error: null,
  place: null,
})

const { t } = useI18n()

const mapContainer = ref<HTMLDivElement | null>(null)

let map: google.maps.Map | null = null

const handleGeoLocationError = () => {
  state.error = t('home_header.component_manual_location_modal.something_went_wrong')

  setTimeout(() => {
    state.error = null
  }, 5000)
}

const onGeoLocationButtonClick = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition((position) => {
      const pos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      }

      state.error = null
      map?.setCenter(pos)
      map?.setZoom(18)
    }, handleGeoLocationError)
  } else {
    handleGeoLocationError()
  }
}

const onZoom = (direction) => {
  map?.setZoom(map?.getZoom() + direction)
}

const { geocode, getLoader } = useGoogleMaps()

const coordsToPlace = async () => {
  const [ place ] = await geocode({ lat: state.lat, lng: state.lng })

  state.place = place
}

const mountMaps = async () => {
  const loader = getLoader()

  const [ { Map } ] = await Promise.all([
    loader.importLibrary('maps'),
  ])

  map = new Map(mapContainer.value, {
    language: getLocale().value,
    center: { lat: state.lat, lng: state.lng },
    zoom: 15,
    streetViewControl: false,
    rotateControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    zoomControl: false,
  })

  map.addListener('center_changed', () => {
    state.lat = map?.getCenter()?.lat()
    state.lng = map?.getCenter()?.lng()
  })
}

const submit = async () => {
  await coordsToPlace()

  const { address_components } = state.place

  const street = address_components.find(comp => comp.types.includes('route'))
  const streetNumber = address_components.find(comp => comp.types.includes('street_number'))
  const postalCode = address_components.find(comp => comp.types.includes('postal_code'))
  const city = address_components.find(comp => comp.types.includes('locality'))
  const country = address_components.find(comp => comp.types.includes('country'))

  const data = {
    query: 'MANUALLY_SELECTED',
    location: {
      street: street?.long_name,
      house_number: streetNumber?.long_name,
      city: city?.long_name,
      zipcode: postalCode?.long_name,
      coordinates: {
        lat: state.lat,
        lng: state.lng,
      },
      country: country?.long_name,
    },
  }

  // ga.trackAddressSearchedPinDrop()

  return emit('submit', data)
}

watch(() => props.open, async (isOpen) => {
  if (!isOpen) return
  mountMaps()
})

onMounted(async () => {
  if (props.open) {
    mountMaps()
  }
})
</script>

<template>
  <ModalNuevo data-test-id="manual-location-modal"
              no-padding
              :open="props.open"
  >
    <div class="manual-location-modal">
      <div ref="mapContainer"
           class="manual-location-modal__map"
           data-test-id="manual-location-modal-map"
      />
      <div class="manual-location-modal__geo-location-button">
        <Button data-test-id="manual-location-modal-geo-button"
                type="secondary"
                @click="onGeoLocationButtonClick"
        >
          <template #icon>
            <Icon name="location-cursor"/>
          </template>
          <template #default>
            {{ t('home_header.component_manual_location_modal.find_my_location') }}
          </template>
        </Button>
      </div>
      <div class="manual-location-modal__zoom-buttons">
        <Button data-test-id="manual-location-modal-zoom-plus-button"
                type="secondary"

                @click="onZoom(1)"
        >
          <template #icon>
            <Icon name="zoom-plus"/>
          </template>
        </Button>
        <Button data-test-id="manual-location-modal-zoom-minus-button"
                type="secondary"

                @click="onZoom(-1)"
        >
          <template #icon>
            <Icon name="zoom-minus"/>
          </template>
        </Button>
      </div>
      <AnimatedFoodMarker class="manual-location-modal__marker"/>
    </div>
    <template #modal-bottom>
      <div v-if="state.error"
           class="manual-location-modal__error"
           data-test-id="geolocation-get-position-error"
      >
        <Icon name="block"/>
        {{ state.error }}
      </div>
      <Button data-test-id="manual-location-modal-submit-button"
              align-right
              @click="submit"
      >
        {{ t('home_header.component_manual_location_modal.save_and_search') }}
      </Button>
    </template>
  </ModalNuevo>
</template>

<style lang="scss">
.manual-location-modal {
  position: relative;

  &__map {
    max-height: 380px;
    height: 80vh;
  }

  &__marker {
    position:  absolute;
    // gets the tip at exactly the position as the original GMaps pin would be
    top:       calc(50% - 5px);
    left:      50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &__geo-location-button {
    position: absolute;
    bottom:   1.7rem;
    left:     50%;
    transform: translateX(-50%);
  }

  &__zoom-buttons {
    position: absolute;
    bottom:   1.7rem;
    right:    1.7rem;
    display:  flex;
    flex-direction: column;
    gap:      0.5rem;
  }

  &__error {
    color:   var(--color-brand-primary);
    display: flex;
    gap:     1rem;
    align-items: center;
  }
}
</style>
